import { getServerSession, getServerSessionExpiresAt } from '../api/sessionAPI';
import { Identity } from '../components/header/models/identity';

import { getViewAsIdentity, getViewAsToken, isViewAs } from '../viewAs/viewAsSession';

/**
 * The bearer token for a request outside the axios clients. HA-447: only a support view-as tab
 * has one (its session token, and nothing once the session has ended). HA-445: a signed-in user
 * has none; their requests are authenticated by the HttpOnly session cookie.
 */
export const getAuthToken = (): string | null => (isViewAs() ? getViewAsToken() : null);

/**
 * The signed-in user's claims. HA-445: from the API's session (GET /auth/session), with the
 * session's expiry as `exp`, so callers see the same shape the decoded ID token had.
 */
export const getIDTokenData = (): Identity => {
  // HA-447: a view-as tab renders as the viewed member, from the session's claim snapshot.
  if (isViewAs()) {
    return getViewAsIdentity();
  }

  const session = getServerSession();
  const expiresAt = getServerSessionExpiresAt();

  if (!session || expiresAt == null) {
    return {} as Identity;
  }

  return { ...session.claims, exp: Math.floor(expiresAt / 1000) };
};

/**
 * Returns the session's expiry in ms since epoch (the earlier of its idle and absolute limits),
 * or null when signed out.
 */
export const getTokenExpiryMs = (): number | null => {
  if (!isViewAs()) {
    return getServerSessionExpiresAt();
  }

  const data = getIDTokenData();

  if (!data?.exp) return null;

  return data.exp * 1000;
};

/**
 * Returns ms remaining until the session expires. Negative if already expired.
 * Returns null when signed out.
 */
export const getMsUntilExpiry = (): number | null => {
  const expiryMs = getTokenExpiryMs();

  if (expiryMs == null) return null;

  return expiryMs - Date.now();
};
